import React, { lazy, Suspense, useContext } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import PixelLoader from "../components/common/PixelLoader";
import PrivateRoute from "./PrivateRoute";
import AppLayout from "../components/layout/AppLayout";
import { AuthContext } from "../context/AuthContext";

// ── Auth ──────────────────────────────────────────────────────────
const LoginPage         = lazy(() => import("../modules/auth/LoginPage"));
const RegisterPage      = lazy(() => import("../modules/auth/RegisterPage"));
const ForgotPassword    = lazy(() => import("../modules/auth/ForgotPassword"));
const TalentTestPage    = lazy(() => import("../modules/auth/TalentTestPage"));

// ── Student ───────────────────────────────────────────────────────
const DashboardPage         = lazy(() => import("../modules/dashboard/DashboardPage"));
const CourseListPage        = lazy(() => import("../modules/courses/CourseListPage"));
const CourseDetailPage      = lazy(() => import("../modules/courses/CourseDetailPage"));
const LessonPlayerPage      = lazy(() => import("../modules/courses/LessonPlayerPage"));
const QuizListPage          = lazy(() => import("../modules/quizzes/QuizListPage"));
const QuizPage              = lazy(() => import("../modules/quizzes/QuizPage"));
const QuizResultPage        = lazy(() => import("../modules/quizzes/QuizResultPage"));
const AssignmentListPage    = lazy(() => import("../modules/assignments/AssignmentListPage"));
const AssignmentDetailPage  = lazy(() => import("../modules/assignments/AssignmentDetailPage"));
const ChallengeListPage     = lazy(() => import("../modules/challenges/ChallengeListPage"));
const ChallengeDetailPage   = lazy(() => import("../modules/challenges/ChallengeDetailPage"));
const LeaderboardPage       = lazy(() => import("../modules/challenges/LeaderboardPage"));
const NotesPage             = lazy(() => import("../modules/notes/NotesPage"));
const InboxPage             = lazy(() => import("../modules/messaging/InboxPage"));
const ConversationPage      = lazy(() => import("../modules/messaging/ConversationPage"));
const ReportsPage           = lazy(() => import("../modules/reports/ReportsPage"));
const AdminReportsPage      = lazy(() => import("../modules/reports/AdminReportsPage"));
const ResumePage            = lazy(() => import("../modules/resume/ResumePage"));
const ResumePreviewPage     = lazy(() => import("../modules/resume/ResumePreviewPage"));
const WalletPage            = lazy(() => import("../modules/wallet/WalletPage"));
const TopUpPage             = lazy(() => import("../modules/wallet/TopUpPage"));
const StudentWalletPage     = lazy(() => import("../modules/wallet/StudentWalletPage"));
const StudentTicketsPage    = lazy(() => import("../modules/tickets/StudentTicketsPage"));
const TicketPage            = lazy(() => import("../modules/tickets/TicketPage"));
const TeacherQuizGradingPage = lazy(() => import("../modules/teacher/TeacherQuizGradingPage"));


// ── جدید ─────────────────────────────────────────────────────────
const ProfilePage           = lazy(() => import("../modules/profile/ProfilePage"));
const SettingsPage          = lazy(() => import("../modules/settings/SettingsPage"));
const NotificationsPage     = lazy(() => import("../modules/notifications/NotificationsPage"));
const CertificatesPage      = lazy(() => import("../modules/certificates/CertificatesPage"));
const CertificateDetailPage = lazy(() => import("../modules/certificates/CertificateDetailPage"));
const SessionCalendarPage   = lazy(() => import("../modules/dashboard/SessionCalendarPage"));
const AdminClassReportsPage = lazy(() => import("../modules/admin/AdminClassReportsPage"));
const SlotManagementPage    = lazy(() => import("../modules/admin/SlotManagementPage"));
const SlotDeleteRequestsPage= lazy(() => import("../modules/admin/SlotDeleteRequestsPage"));

// ── Assignment / Note ─────────────────────────────────────────────
const AssignmentBuilderPage = lazy(() => import("../modules/assignments/AssignmentBuilderPage"));
// fix 1: syntax بد { AssignmentManagePage } = { ... } اصلاح شد
const AssignmentManagePage  = lazy(() =>
  import("../modules/assignments/AssignmentBuilderPage").then(m => ({ default: m.AssignmentManagePage }))
);
const AssignmentStudentPage = lazy(() => import("../modules/assignments/AssignmentStudentPage"));
const NoteBuilderPage       = lazy(() => import("../modules/notes/NoteBuilderPage"));
const NoteListPage          = lazy(() => import("../modules/notes/NoteListPage"));
const NoteViewPage          = lazy(() => import("../modules/notes/NoteViewPage"));

// ── Teacher ───────────────────────────────────────────────────────
const TeacherDashboardPage    = lazy(() => import("../modules/teacher/TeacherDashboardPage"));
const ManageClassesPage       = lazy(() => import("../modules/teacher/ManageClassesPage"));
const StudentRosterPage       = lazy(() => import("../modules/teacher/StudentRosterPage"));
const GradeAssignmentsPage    = lazy(() => import("../modules/teacher/GradeAssignmentsPage"));
const QuizBuilderPage         = lazy(() => import("../modules/teacher/QuizBuilderPage"));
const TeacherAvailabilityPage = lazy(() => import("../modules/teacher/TeacherAvailabilityPage"));
const TeacherClassSchedule    = lazy(() => import("../modules/teacher/TeacherClassSchedule"));
const SalarySettlementPage    = lazy(() => import("../modules/teacher/SalarySettlementPage"));
const TeacherSalaryReportPDF  = lazy(() => import("../modules/teacher/TeacherSalaryReportPDF"));

// ── Admin ─────────────────────────────────────────────────────────
const AdminDashboardPage      = lazy(() => import("../modules/admin/AdminDashboardPage"));
const UserManagementPage      = lazy(() => import("../modules/admin/UserManagementPage"));
const CourseManagementPage    = lazy(() => import("../modules/admin/CourseManagementPage"));
const CourseBuilderPage       = lazy(() => import("../modules/admin/CourseBuilderPage"));
// fix 2: QuizManagementPage inline component → lazy import به فایل جدید
const QuizManagementPage      = lazy(() => import("../modules/admin/QuizManagementPage"));
const ChallengeManagementPage = lazy(() => import("../modules/admin/ChallengeManagementPage"));
const WalletManagementPage    = lazy(() => import("../modules/admin/WalletManagementPage"));
const FinancialReportPage     = lazy(() => import("../modules/admin/FinancialReportPage"));
const WalletRequestsPage      = lazy(() => import("../modules/admin/WalletRequestsPage"));
const AssignSupportPage       = lazy(() => import("../modules/admin/AssignSupportPage"));
const AdminSettlementPage     = lazy(() => import("../modules/admin/AdminSettlementPage"));

// ── HR ────────────────────────────────────────────────────────────
const HRDashboardPage    = lazy(() => import("../modules/hr/HRDashboardPage"));
const EmployeeListPage   = lazy(() => import("../modules/hr/EmployeeListPage"));
const EmployeeDetailPage = lazy(() => import("../modules/hr/EmployeeDetailPage"));

// ── Support ───────────────────────────────────────────────────────
const SupportDashboardPage = lazy(() => import("../modules/support/SupportDashboardPage"));
const StudentMonitorPage   = lazy(() => import("../modules/support/StudentMonitorPage"));
const AlertsPage           = lazy(() => import("../modules/support/AlertsPage"));
const SupportMonitorPage   = lazy(() => import("../modules/support/SupportMonitorPage"));
const SupportCalendarPage  = lazy(() => import("../modules/support/SupportCalendarPage"));

// ── Scheduling ────────────────────────────────────────────────────
const EnrollSelectTeacherPage = lazy(() => import("../modules/courses/EnrollSelectTeacherPage"));
const TeacherComparisonPage   = lazy(() => import("../modules/courses/TeacherComparisonPage"));
const SessionReportView       = lazy(() => import("../modules/courses/SessionReportView"));
const StudentClassSchedule    = lazy(() => import("../modules/dashboard/StudentClassSchedule"));

// ── Home (صفحه عمومی قبل از لاگین) ──────────────────────────────────
const HomePage = lazy(() => import("../modules/home/HomePage"));

// ── Showcase (آثار دانش‌آموزان) ─────────────────────────────────────
const ShowcaseReviewPage = lazy(() => import("../modules/showcase/ShowcaseReviewPage"));

// ── Misc ──────────────────────────────────────────────────────────
const NotFoundPage = lazy(() => import("../modules/misc/NotFoundPage"));
const PageFallback = () => <PixelLoader variant="bar" text="بارگذاری صفحه..." fullPage />;

// ════════════════════════════════════════════════════════════════════
// LayoutWrapper — AppLayout (همبرگر + Drawer) دور همه صفحات به‌جز
// صفحات public پیچیده می‌شود. چون <Routes> فقط عنصر Route ای که match
// شده را به‌عنوان فرزند رندر می‌کند، پیچیدن یک‌بار دور <Routes> دقیقاً
// همان اثر پیچیدن تک‌تک Route ها را دارد — بدون ریسک فراموش‌کردن یکی
// از ده‌ها Route.
// ════════════════════════════════════════════════════════════════════
const NO_LAYOUT = ["/", "/login", "/register", "/forgot-password", "/talent-test"];

function LayoutWrapper({ children }) {
  const location = useLocation();
  const skip =
    NO_LAYOUT.some(p => location.pathname === p) ||
    location.pathname.startsWith("/resume/public/");
  if (skip) return <>{children}</>;
  return <AppLayout>{children}</AppLayout>;
}

// ── RootRoute — تصمیم می‌گیره "/" چی نشون بده ────────────────────────
// لاگین‌کرده → مستقیم داشبورد خودش؛ مهمون → صفحه‌ی عمومی HomePage
const DASHBOARD_BY_ROLE = {
  admin:   "/admin/dashboard",
  teacher: "/teacher/dashboard",
  support: "/support/dashboard",
  student: "/dashboard",
};

function RootRoute() {
  const context = useContext(AuthContext);
  if (!context) return <PixelLoader variant="bar" text="بارگذاری..." fullPage />;

  const { state } = context;
  if (state.isLoading) {
    return <PixelLoader variant="bar" text="در حال بارگذاری..." fullPage />;
  }
  if (state.isAuthenticated) {
    const role = state.user?.role;
    return <Navigate to={DASHBOARD_BY_ROLE[role] ?? "/dashboard"} replace />;
  }
  return <HomePage />;
}

const AppRouter = () => (
  <Suspense fallback={<PageFallback />}>
    <LayoutWrapper>
      <Routes>

        {/* ── Public ── */}
        <Route path="/login"           element={<LoginPage />} />
        <Route path="/register"        element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/resume/public/:username" element={<ResumePreviewPage />} />

        {/* ── First login ── */}
        <Route path="/talent-test" element={<PrivateRoute><TalentTestPage /></PrivateRoute>} />
        <Route path="/wallet/topup" element={<PrivateRoute><TopUpPage /></PrivateRoute>} />

        {/* ══ STUDENT ══ */}
        <Route path="/dashboard" element={<PrivateRoute allowedRoles={["student"]}><DashboardPage /></PrivateRoute>} />
        <Route path="/courses"   element={<PrivateRoute allowedRoles={["student","teacher","admin","support"]}><CourseListPage /></PrivateRoute>} />
        <Route path="/courses/:courseId" element={<PrivateRoute allowedRoles={["student","teacher","admin","support"]}><CourseDetailPage /></PrivateRoute>} />
        <Route path="/courses/:courseId/lessons/:lessonId" element={<PrivateRoute allowedRoles={["student","teacher","admin"]}><LessonPlayerPage /></PrivateRoute>} />
        <Route path="/courses/:courseId/select-teacher"   element={<PrivateRoute allowedRoles={["student"]}><EnrollSelectTeacherPage /></PrivateRoute>} />
        <Route path="/courses/:courseId/compare-teachers" element={<PrivateRoute allowedRoles={["student"]}><TeacherComparisonPage /></PrivateRoute>} />
        <Route path="/my-classes"                          element={<PrivateRoute allowedRoles={["student"]}><StudentClassSchedule /></PrivateRoute>} />
        <Route path="/my-classes/:occurrenceId/report"    element={<PrivateRoute allowedRoles={["student"]}><SessionReportView /></PrivateRoute>} />
        <Route path="/quizzes"              element={<PrivateRoute allowedRoles={["student"]}><QuizListPage /></PrivateRoute>} />
        <Route path="/quizzes/:quizId"      element={<PrivateRoute allowedRoles={["student"]}><QuizPage /></PrivateRoute>} />
        <Route path="/quizzes/:quizId/result/:sessionId" element={<PrivateRoute allowedRoles={["student"]}><QuizResultPage /></PrivateRoute>} />
        <Route path="/assignments"          element={<PrivateRoute allowedRoles={["student","teacher","admin"]}><AssignmentStudentPage /></PrivateRoute>} />
        <Route path="/assignments/:assignmentId" element={<PrivateRoute allowedRoles={["student"]}><AssignmentDetailPage /></PrivateRoute>} />
        <Route path="/challenges"           element={<PrivateRoute allowedRoles={["student","teacher","admin","support"]}><ChallengeListPage /></PrivateRoute>} />
        <Route path="/challenges/:challengeId" element={<PrivateRoute allowedRoles={["student"]}><ChallengeDetailPage /></PrivateRoute>} />
        <Route path="/challenges/leaderboard" element={<PrivateRoute allowedRoles={["student"]}><LeaderboardPage /></PrivateRoute>} />
        <Route path="/notes"    element={<PrivateRoute allowedRoles={["student"]}><NotesPage /></PrivateRoute>} />
        <Route path="/notes/view/:noteId" element={<PrivateRoute allowedRoles={["student","teacher","admin","support"]}><NoteViewPage /></PrivateRoute>} />
        <Route path="/messages" element={<PrivateRoute allowedRoles={["student","teacher","support"]}><InboxPage /></PrivateRoute>} />
        <Route path="/messages/:threadId" element={<PrivateRoute allowedRoles={["student","teacher","support"]}><ConversationPage /></PrivateRoute>} />
        <Route path="/reports"  element={<PrivateRoute allowedRoles={["student"]}><ReportsPage /></PrivateRoute>} />
        <Route path="/resume"   element={<PrivateRoute allowedRoles={["student"]}><ResumePage /></PrivateRoute>} />
        <Route path="/resume/preview" element={<PrivateRoute allowedRoles={["student"]}><ResumePreviewPage /></PrivateRoute>} />
        <Route path="/wallet"   element={<PrivateRoute allowedRoles={["student","teacher","admin","support"]}><StudentWalletPage /></PrivateRoute>} />
        <Route path="/tickets"  element={<PrivateRoute allowedRoles={["student"]}><StudentTicketsPage /></PrivateRoute>} />
        <Route path="/ticketing" element={<PrivateRoute><TicketPage /></PrivateRoute>} />
        <Route path="/teacher/quiz-grading" element={<PrivateRoute allowedRoles={["teacher","admin"]}><TeacherQuizGradingPage /></PrivateRoute>} />
        <Route path="/quizzes/:quizId/result/:attemptId" element={<PrivateRoute><QuizResultPage /></PrivateRoute>} />

        {/* ── صفحات مشترک همه نقش‌ها ── */}
        <Route path="/profile"        element={<PrivateRoute><ProfilePage /></PrivateRoute>} />
        <Route path="/settings"       element={<PrivateRoute><SettingsPage /></PrivateRoute>} />
        <Route path="/notifications"  element={<PrivateRoute><NotificationsPage /></PrivateRoute>} />
        <Route path="/certificates"   element={<PrivateRoute allowedRoles={["student"]}><CertificatesPage /></PrivateRoute>} />
        <Route path="/certificates/:certId" element={<PrivateRoute allowedRoles={["student"]}><CertificateDetailPage /></PrivateRoute>} />
        <Route path="/calendar"       element={<PrivateRoute allowedRoles={["student","teacher","support"]}><SessionCalendarPage /></PrivateRoute>} />

        {/* ══ TEACHER ══ */}
        <Route path="/teacher/dashboard"    element={<PrivateRoute allowedRoles={["teacher"]}><TeacherDashboardPage /></PrivateRoute>} />
        <Route path="/teacher/classes"      element={<PrivateRoute allowedRoles={["teacher"]}><ManageClassesPage /></PrivateRoute>} />
        <Route path="/teacher/students"     element={<PrivateRoute allowedRoles={["teacher"]}><StudentRosterPage /></PrivateRoute>} />
        <Route path="/teacher/assignments"  element={<PrivateRoute allowedRoles={["teacher"]}><GradeAssignmentsPage /></PrivateRoute>} />
        <Route path="/teacher/reports"      element={<PrivateRoute allowedRoles={["teacher"]}><ReportsPage /></PrivateRoute>} />
        <Route path="/teacher/availability" element={<PrivateRoute allowedRoles={["teacher"]}><TeacherAvailabilityPage /></PrivateRoute>} />
        <Route path="/teacher/schedule"     element={<PrivateRoute allowedRoles={["teacher"]}><TeacherClassSchedule /></PrivateRoute>} />
        <Route path="/teacher/salary"       element={<PrivateRoute allowedRoles={["teacher"]}><SalarySettlementPage /></PrivateRoute>} />
        <Route path="/teacher/salary/report" element={<PrivateRoute allowedRoles={["teacher"]}><TeacherSalaryReportPDF /></PrivateRoute>} />

        {/* ── Teacher shared ── */}
        <Route path="/assignments/builder" element={<PrivateRoute allowedRoles={["admin","teacher"]}><AssignmentBuilderPage /></PrivateRoute>} />
        <Route path="/assignments/manage"  element={<PrivateRoute allowedRoles={["admin","teacher"]}><AssignmentManagePage /></PrivateRoute>} />
        <Route path="/notes/builder"       element={<PrivateRoute allowedRoles={["admin","teacher","staff"]}><NoteBuilderPage /></PrivateRoute>} />
        <Route path="/notes/list"          element={<PrivateRoute allowedRoles={["admin","teacher","staff"]}><NoteListPage /></PrivateRoute>} />
        <Route path="/quiz-builder"        element={<PrivateRoute allowedRoles={["admin","teacher"]}><QuizBuilderPage /></PrivateRoute>} />
        <Route path="/teacher/quiz-builder" element={<PrivateRoute allowedRoles={["teacher"]}><QuizBuilderPage /></PrivateRoute>} />

        {/* ══ ADMIN ══ */}
        <Route path="/admin/dashboard"      element={<PrivateRoute allowedRoles={["admin"]}><AdminDashboardPage /></PrivateRoute>} />
        <Route path="/admin/users"          element={<PrivateRoute allowedRoles={["admin"]}><UserManagementPage /></PrivateRoute>} />
        <Route path="/admin/courses"        element={<PrivateRoute allowedRoles={["admin"]}><CourseManagementPage /></PrivateRoute>} />
        <Route path="/admin/course-builder" element={<PrivateRoute allowedRoles={["admin","teacher"]}><CourseBuilderPage /></PrivateRoute>} />
        <Route path="/admin/challenges"     element={<PrivateRoute allowedRoles={["admin"]}><ChallengeManagementPage /></PrivateRoute>} />
        <Route path="/admin/wallet"         element={<PrivateRoute allowedRoles={["admin"]}><WalletManagementPage /></PrivateRoute>} />
        <Route path="/admin/wallet-deposits" element={<PrivateRoute allowedRoles={["admin"]}><WalletRequestsPage /></PrivateRoute>} />
        <Route path="/admin/wallet-requests" element={<PrivateRoute allowedRoles={["admin"]}><WalletRequestsPage /></PrivateRoute>} />
        <Route path="/admin/financial"      element={<PrivateRoute allowedRoles={["admin"]}><FinancialReportPage /></PrivateRoute>} />
        <Route path="/admin/reports"        element={<PrivateRoute allowedRoles={["admin"]}><AdminReportsPage /></PrivateRoute>} />
        <Route path="/admin/quizzes"        element={<PrivateRoute allowedRoles={["admin","teacher"]}><QuizManagementPage /></PrivateRoute>} />
        <Route path="/admin/assign-support" element={<PrivateRoute allowedRoles={["admin"]}><AssignSupportPage /></PrivateRoute>} />
        <Route path="/admin/settlement"     element={<PrivateRoute allowedRoles={["admin"]}><AdminSettlementPage /></PrivateRoute>} />
        <Route path="/admin/hr"             element={<PrivateRoute allowedRoles={["admin"]}><HRDashboardPage /></PrivateRoute>} />
        <Route path="/admin/hr/employees"   element={<PrivateRoute allowedRoles={["admin"]}><EmployeeListPage /></PrivateRoute>} />
        <Route path="/admin/hr/employees/:employeeId" element={<PrivateRoute allowedRoles={["admin"]}><EmployeeDetailPage /></PrivateRoute>} />

        {/* ══ SUPPORT ══ */}
        <Route path="/support/dashboard" element={<PrivateRoute allowedRoles={["support"]}><SupportDashboardPage /></PrivateRoute>} />
        <Route path="/support/students"  element={<PrivateRoute allowedRoles={["support"]}><StudentMonitorPage /></PrivateRoute>} />
        <Route path="/support/alerts"    element={<PrivateRoute allowedRoles={["support"]}><AlertsPage /></PrivateRoute>} />
        <Route path="/support/monitor"   element={<PrivateRoute allowedRoles={["support","admin"]}><SupportMonitorPage /></PrivateRoute>} />
        <Route path="/support/calendar"  element={<PrivateRoute allowedRoles={["support","admin"]}><SupportCalendarPage /></PrivateRoute>} />

        {/* ── Admin extra ── */}
        <Route path="/admin/class-reports" element={<PrivateRoute allowedRoles={["admin"]}><AdminClassReportsPage /></PrivateRoute>} />
        <Route path="/admin/slots" element={<PrivateRoute allowedRoles={["admin"]}><SlotManagementPage /></PrivateRoute>} />
        <Route path="/admin/slot-delete-requests" element={<PrivateRoute allowedRoles={["admin"]}><SlotDeleteRequestsPage /></PrivateRoute>} />

        {/* ── Showcase (آثار دانش‌آموزان) — بررسی توسط معلم/ادمین ── */}
        <Route path="/teacher/showcase-review" element={<PrivateRoute allowedRoles={["teacher"]}><ShowcaseReviewPage /></PrivateRoute>} />
        <Route path="/admin/showcase-review"   element={<PrivateRoute allowedRoles={["admin"]}><ShowcaseReviewPage /></PrivateRoute>} />

        {/* ── Root ── */}
        <Route path="/" element={<RootRoute />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </LayoutWrapper>
  </Suspense>
);

export default AppRouter;